import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { KnowledgeBaseActionDto } from '@/Domain/api/Action/dto/KnowledgeBaseAction.dto';

export const useActivateKnowledgeBaseAction = (action: ActionDto) => {
  const { t } = useTranslation('knowledge_base_action');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.basic.patch],
    mutationFn: ({ is_active, id }: { is_active: boolean; id: number }) =>
      ActionsApi.update(id, { is_active: is_active }),
    onSuccess: (updatedAction) => {
      void queryClient.setQueryData(
        [QueryKey.actions.knowledgeBase.get, updatedAction.id],
        (prev: KnowledgeBaseActionDto) => ({ ...prev, ...updatedAction }),
      );
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });

      queryClient.setQueryData([QueryKey.actions.basic.list], (prev: ActionDto[]) =>
        prev.map((actionItem) =>
          actionItem.id === action.id
            ? { ...actionItem, is_active: !actionItem.is_active }
            : actionItem,
        ),
      );

      if (updatedAction.is_active) {
        toast.info(t('toast.active_enabled'));
      } else {
        toast.info(t('toast.active_disabled'));
      }
    },
  });

  const handleActive = (isActive: boolean) => {
    mutation.mutate({ is_active: isActive, id: action.id });
  };

  return {
    handleActive,
  };
};
